import { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { FONT_SIZE, FONT_WEIGHT, SPACING } from 'constants/design-system';

import Addons from 'modules/ChefScene/components/Addons';
import { StatusButtons } from 'modules/GridView/components/OrderDetails/StatusButtons';
import { useAppSelector } from 'store/hooks';
import { NotificationsSelectors } from 'store/Notifications';
import { OrderSelectors } from 'store/Orders';
import { getOrderInternalStatus } from 'store/Orders/utils';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { useCurrencyString } from 'util/helpers/currency';
import { useItemDisplayName } from 'util/hooks/useItemDisplayName';
import { BaseText } from 'components/BaseText';

import type {
  ItemPreparationStatuses,
  OrderInternalStatuses,
} from 'store/Orders/enums';
import type { Order, OrderItem } from 'store/Orders/types';

type OrderItemDetailProps = {
  itemUuid: string;
  // order: Order;
  shouldDifferentiateLayout?: boolean;
  // orderInternalStatus: OrderInternalStatuses;

  onStatusChanged?: (props: {
    orderId: string;
    itemId: string;
    mode: 'single' | 'group';
    startDate: Date;
    endDate: Date;
    status: ItemPreparationStatuses;
    screen: string | undefined;
  }) => void;

  onPress?: () => void;
  onPressedAction?: () => void;
  order: Order;
  items: OrderItem[];
  itemOverride?: OrderItem;
};

export const OrderItemDetail = (props: OrderItemDetailProps) => {
  const {
    itemUuid,
    // orderInternalStatus,
    onPress,
    onPressedAction,
    onStatusChanged,
    shouldDifferentiateLayout,
    items,
    order,
    itemOverride,
  } = props;

  const orderInternalStatus: OrderInternalStatuses = useMemo(() => {
    return getOrderInternalStatus(order, items);
  }, [order, items]);

  const item = useAppSelector((state) =>
    itemOverride
      ? itemOverride
      : OrderSelectors.selectItemById(state, itemUuid),
  );

  const interfaceSettingsFeatureFlag = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );

  const { shouldDisplayItemPricing } = useAppSelector(
    SettingSelectors.selectItemListInterfaceSettings,
  );
  const { shouldDisplayIndividualActionButton } = useAppSelector(
    SettingSelectors.selectActionButtonsInterfaceSettings,
  );

  const { itemDisplayName } = useItemDisplayName({
    itemDisplayName: item?.display_names ?? null,
  });

  const getCurrencyString = useCurrencyString();

  const hasUnacceptedNotifications = useAppSelector((state) =>
    item
      ? NotificationsSelectors.selectOrderItemHasUnattended(state, item.uuid)
      : undefined,
  );

  return (
    <View
      style={{
        padding: SPACING.theme[8],
        gap: SPACING.theme[8],
        flexGrow: 1,
        maxHeight: '100%',
      }}
    >
      <ScrollView>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
          }}
        >
          <BaseText
            fontWeight={FONT_WEIGHT.theme.medium}
            fontSize={32}
            color={'#222'}
          >
            {`${Number.isInteger(item?.quantity) ? item?.quantity : 1} -`}{' '}
          </BaseText>
          <BaseText
            fontWeight={FONT_WEIGHT.theme.medium}
            fontSize={32}
            color={'#222'}
          >
            {itemDisplayName ?? item?.name}
            <BaseText
              fontWeight={FONT_WEIGHT.theme.medium}
              fontSize={32}
              color={'#333'}
            >
              {shouldDisplayItemPricing &&
                interfaceSettingsFeatureFlag.item_list.item_pricing &&
                item?.price != null &&
                ` - ${getCurrencyString(item?.price)}`}
            </BaseText>
          </BaseText>
        </View>
        {item?.addons && (
          <View>
            <Addons
              addonsDisplayNames={item.addons_display_names}
              addons={item?.addons}
              addonsType={item?.addons_type}
              fontSize={FONT_SIZE.theme['3xl']}
            />
          </View>
        )}
        {item?.special_instructions && (
          <View
            style={{
              marginTop: 10,
              backgroundColor: '#ffbec0',
              borderRadius: 5,
              padding: 5,
              flexDirection: 'row',
            }}
          >
            <BaseText
              fontWeight={FONT_WEIGHT.theme.medium}
              fontSize={FONT_SIZE.theme.base}
            >
              {item.special_instructions}
            </BaseText>
          </View>
        )}
      </ScrollView>

      {shouldDisplayIndividualActionButton && item && (
        <View
          style={{
            // FIXME: refactor ActionButton component. it doens't register its innert height. now using the hardcoded value
            height: 76,
            marginTop: 'auto',
          }}
        >
          <StatusButtons
            fullWidth
            order={order}
            item={item}
            orderInternalStatus={orderInternalStatus}
            hasUnacceptedNotifications={hasUnacceptedNotifications}
            onStatusChanged={onStatusChanged}
            onPressedAction={onPressedAction}
          />
        </View>
      )}
    </View>
  );
};
